// Skill tree (meta layer). Skill points are earned when a settlement falls and
// spent here for permanent bonuses that carry into every future run. This is
// the stable floor the blueprint describes: the skill tree never resets, while
// age cards reshuffle each run.
//
// The tree is UNLOCKABLE: a skill with a `requires` stays hidden until its
// prerequisite is owned at the given level, then it reveals as available. Roots
// (no `requires`) are always visible so a fresh player always has something to
// spend on, including defense. Ordering below is branch-contiguous (root then
// its children) so a newly revealed node appears next to the parent that
// unlocked it.

/** Permanent, cross-run bonuses. Multipliers default to 1, flats to 0, except
 *  offlineCapHours which seeds from the balance default at apply time. */
export interface MetaPerks {
  startWood: number;
  startStone: number;
  startOre: number;
  startPopulation: number;
  startSpoils: number;
  globalProductionMult: number;
  globalKnowledgeMult: number;
  globalBuildCostMult: number;
  harvestDamageMult: number;
  harvestRadiusMult: number;
  defenseMult: number;
  autoCollectMult: number;
  offlineCapHours: number;
}

export function freshMetaPerks(offlineCapHours: number): MetaPerks {
  return {
    startWood: 0,
    startStone: 0,
    startOre: 0,
    startPopulation: 0,
    startSpoils: 0,
    globalProductionMult: 1,
    globalKnowledgeMult: 1,
    globalBuildCostMult: 1,
    harvestDamageMult: 1,
    harvestRadiusMult: 1,
    defenseMult: 1,
    autoCollectMult: 1,
    offlineCapHours,
  };
}

export interface SkillDef {
  id: string;
  name: string;
  icon: string;
  desc: string;
  maxLevel: number;
  baseCost: number; // skill points for the first level
  costGrowth: number; // cost multiplier per owned level
  /** Prerequisite gate: this skill stays hidden until skill `id` is owned at
   *  `level` or higher. Omitted on roots (always visible). A skill the player
   *  already owns is shown regardless (see skillVisible) so old saves never
   *  strand invested points. */
  requires?: { id: string; level: number };
  /** Applies this skill's full effect for the given owned level. */
  apply: (p: MetaPerks, level: number) => void;
}

export const SKILLS: SkillDef[] = [
  // ---- Ekonomi (economy) branch ----
  {
    id: 'bountiful-start',
    name: 'Bountiful Start',
    icon: '🌾',
    desc: 'Begin each run with more wood, stone, and ore.',
    maxLevel: 10,
    baseCost: 1,
    costGrowth: 1.55,
    apply: (p, lvl) => {
      // Chunky enough that level 1 visibly accelerates the next run's opening
      // (~25s of early harvesting skipped), not a rounding error.
      p.startWood += 25 * lvl;
      p.startStone += 20 * lvl;
      p.startOre += 10 * lvl;
    },
  },
  {
    id: 'industrious',
    name: 'Industrious',
    icon: '🏭',
    desc: '+8% global production per level.',
    maxLevel: 15,
    baseCost: 2,
    costGrowth: 1.45,
    requires: { id: 'bountiful-start', level: 1 },
    apply: (p, lvl) => {
      p.globalProductionMult *= Math.pow(1.08, lvl);
    },
  },
  {
    id: 'master-mason',
    name: 'Master Mason',
    icon: '🏛️',
    desc: 'Buildings cost 5% less per level.',
    maxLevel: 8,
    baseCost: 2,
    costGrowth: 1.55,
    requires: { id: 'industrious', level: 3 },
    apply: (p, lvl) => {
      p.globalBuildCostMult *= Math.pow(0.95, lvl);
    },
  },
  {
    id: 'swift-hands',
    name: 'Swift Hands',
    icon: '⚡',
    desc: '+10% auto-collection per level.',
    maxLevel: 10,
    baseCost: 2,
    costGrowth: 1.5,
    requires: { id: 'industrious', level: 3 },
    apply: (p, lvl) => {
      p.autoCollectMult *= Math.pow(1.1, lvl);
    },
  },
  {
    id: 'settlers',
    name: 'Settlers',
    icon: '👪',
    desc: 'Begin each run with extra population.',
    maxLevel: 8,
    baseCost: 1,
    costGrowth: 1.7,
    requires: { id: 'bountiful-start', level: 2 },
    apply: (p, lvl) => {
      p.startPopulation += lvl;
    },
  },
  {
    id: 'sharp-tools',
    name: 'Sharp Tools',
    icon: '🪓',
    desc: '+10% harvest damage per level.',
    maxLevel: 12,
    baseCost: 2,
    costGrowth: 1.45,
    requires: { id: 'settlers', level: 1 },
    apply: (p, lvl) => {
      p.harvestDamageMult *= Math.pow(1.1, lvl);
    },
  },
  {
    id: 'wide-reach',
    name: 'Wide Reach',
    icon: '🎯',
    desc: '+10% harvest circle radius per level.',
    maxLevel: 10,
    baseCost: 2,
    costGrowth: 1.55,
    requires: { id: 'sharp-tools', level: 3 },
    apply: (p, lvl) => {
      p.harvestRadiusMult *= Math.pow(1.1, lvl);
    },
  },
  // ---- Ordu (military) branch ----
  {
    id: 'fortified',
    name: 'Fortified',
    icon: '🏰',
    desc: '+12% settlement defense per level.',
    maxLevel: 12,
    baseCost: 2,
    costGrowth: 1.5,
    apply: (p, lvl) => {
      p.defenseMult *= Math.pow(1.12, lvl);
    },
  },
  {
    id: 'war-chest',
    name: 'War Chest',
    icon: '💰',
    desc: 'Begin each run with spoils to spend on technology.',
    maxLevel: 8,
    baseCost: 2,
    costGrowth: 1.6,
    requires: { id: 'fortified', level: 2 },
    apply: (p, lvl) => {
      p.startSpoils += 8 * lvl; // vs post-nerf tech costs (Conscription 40, Bulwark 34,
      // cheapest 20): one level is a head start, banking a defense tech takes several
    },
  },
  // ---- Bilgelik (knowledge) branch ----
  {
    id: 'archivist',
    name: 'Archivist',
    icon: '📖',
    desc: '+10% knowledge and tech defense per level.',
    maxLevel: 10,
    baseCost: 2,
    costGrowth: 1.5,
    apply: (p, lvl) => {
      p.globalKnowledgeMult *= Math.pow(1.1, lvl);
    },
  },
  {
    id: 'long-memory',
    name: 'Long Memory',
    icon: '🕰️',
    desc: '+1 hour offline cap per level.',
    maxLevel: 8,
    baseCost: 3,
    costGrowth: 1.65,
    requires: { id: 'archivist', level: 2 },
    apply: (p, lvl) => {
      p.offlineCapHours += lvl;
    },
  },
];

export const SKILL_BY_ID: Record<string, SkillDef> = {};
for (const s of SKILLS) SKILL_BY_ID[s.id] = s;

/** Skill-point cost to take a skill from `level` to `level + 1`. */
export function skillCost(def: SkillDef, level: number): number {
  return Math.ceil(def.baseCost * Math.pow(def.costGrowth, level));
}

/** Whether a skill's prerequisite is currently satisfied (or it is a root). */
export function skillUnlocked(def: SkillDef, levels: Record<string, number>): boolean {
  if (!def.requires) return true;
  return (levels[def.requires.id] ?? 0) >= def.requires.level;
}

/** Whether a skill should be VISIBLE in the tree. Visible if its prerequisite
 *  is met (unlocked) OR the player already owns it — the latter keeps old
 *  saves from hiding a skill that already holds invested points. */
export function skillVisible(def: SkillDef, levels: Record<string, number>): boolean {
  return skillUnlocked(def, levels) || (levels[def.id] ?? 0) > 0;
}

/** Rebuilds the full MetaPerks object from owned skill levels. */
export function computeMetaPerks(
  levels: Record<string, number>,
  baseOfflineCapHours: number,
): MetaPerks {
  const perks = freshMetaPerks(baseOfflineCapHours);
  for (const def of SKILLS) {
    const lvl = levels[def.id] ?? 0;
    if (lvl > 0) def.apply(perks, lvl);
  }
  return perks;
}
